import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { api } from '@/App';
import { useDarkMode } from '@/hooks/useDarkMode';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/auth/PasswordInput';

export default function ResetPassword() {
  const darkMode = useDarkMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reset your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goSignIn = () => {
    navigate('/');
    setTimeout(() => window.dispatchEvent(new CustomEvent('mementa:open-auth')), 400);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 transition-colors" style={{ background: darkMode.bgPrimary }}>
      <div
        className="w-full max-w-md p-8 rounded-2xl transition-colors"
        style={{
          background: darkMode.bgSecondary,
          boxShadow: darkMode.isDark ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(0,0,0,0.15)'
        }}
        data-testid="reset-password-card"
      >
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5" style={{ background: '#FED7A5' }}>
          {done ? <CheckCircle2 className="w-7 h-7" style={{ color: '#20212B' }} /> : <KeyRound className="w-7 h-7" style={{ color: '#20212B' }} />}
        </div>
        <h1 className="text-3xl mb-3" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}>
          {done ? 'Password Updated' : 'Choose a New Password'}
        </h1>

        {done ? (
          <>
            <p className="text-sm mb-6" style={{ color: darkMode.textSecondary }}>
              Your password has been reset. Sign in with your new password to pick up where you left off.
            </p>
            <Button
              onClick={goSignIn}
              className="w-full"
              style={{ background: '#9E6752', color: 'white', borderRadius: '20px', padding: '12px' }}
              data-testid="reset-success-signin-btn"
            >
              Sign In
            </Button>
          </>
        ) : !token ? (
          <p className="text-sm" style={{ color: darkMode.textSecondary }} data-testid="reset-missing-token">
            This reset link is missing its token. Please use the link from your email, or request a new one from the sign-in screen.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password" style={{ color: darkMode.textSecondary }}>New password</Label>
              <PasswordInput
                id="new-password"
                testId="reset-password-input"
                toggleTestId="reset-password-toggle"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 transition-colors"
                style={{ color: darkMode.textPrimary, background: darkMode.bgPrimary, borderColor: darkMode.borderPrimary }}
                iconColor={darkMode.textSecondary}
              />
              <p className="text-xs mt-1" style={{ color: darkMode.textSecondary }}>Minimum 8 characters</p>
            </div>
            <div>
              <Label htmlFor="confirm-password" style={{ color: darkMode.textSecondary }}>Confirm new password</Label>
              <PasswordInput
                id="confirm-password"
                testId="reset-confirm-input"
                toggleTestId="reset-confirm-toggle"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 transition-colors"
                style={{ color: darkMode.textPrimary, background: darkMode.bgPrimary, borderColor: darkMode.borderPrimary }}
                iconColor={darkMode.textSecondary}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full mt-2"
              style={{ background: '#9E6752', color: 'white', borderRadius: '20px', padding: '12px' }}
              data-testid="reset-password-submit"
            >
              {loading ? 'Updating...' : 'Reset Password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
